/**
 * The runs lens: what the map reveals under an arrow. The integration to look at and failed/all as pills (every
 * count with its noun), a table with a row per run (process, when, how long, how many documents, status) whose
 * time, duration and status columns have fixed widths so the rows line up, its steps in place on request, the run
 * itself on click. A row, the failed pill and the counts are about the documents the search found in the run (how many,
 * whether one of them failed); the whole run — its other documents and failures — is in its steps and its peek, said so.
 * Ported from components/data/Runs.tsx (RunRow, Steps), re-skinned.
 */
import { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import type { Execution, Run, Runs } from '../../lib/api.ts';
import { useAsync } from '../../lib/hooks.ts';
import { count, duration } from '../../lib/format.ts';
import { stripSystem } from '../../lib/systems.ts';
import { ChevronIcon } from '../../components/icons.tsx';
import { Chip, Glyph, IconButton, NounIcon, Pill, SystemBadge, cx, noun } from '../../design/primitives.tsx';
import { Counted, NounCount, EmptyLens, LensFrame, MorePill, PillTrack, StatusWord, compact, plural, runStatus, type MessageRef } from './common.tsx';

const SHOWN = 50;
const NO_PROCESS = '(no process)';

export function RunsLens({ runs, processes, process, failedOnly, expanded, onExpand, onRun, onMessage, loadExecution, onProcess, onFailedOnly, total, onClose }: {
  /** The runs found, newest first. */
  runs: Run[];
  processes: Runs['processes'];
  /** Only this integration's runs at first (an arrow chosen on the map); null = all. */
  process?: string | null;
  failedOnly?: boolean;
  /** The run whose steps are open in place, and its toggle: the page keeps it in the URL. */
  expanded?: string | null;
  onExpand?: (executionId: string | null) => void;
  /** A row opens the run (a peek). */
  onRun: (executionId: string, run: Run) => void;
  onMessage: (ref: MessageRef) => void;
  /** Loads a run's steps for the in-place expansion; without it the rows only open the run. */
  loadExecution?: (executionId: string) => Promise<Execution>;
  /** The choosers changed; the page may keep them in the URL and pass them back. */
  onProcess?: (process: string | null) => void;
  onFailedOnly?: (failedOnly: boolean) => void;
  /** How many runs there are in all, when `runs` is the newest few of them. */
  total?: number;
  onClose?: () => void;
}) {
  const [proc, setProc] = useState<string | null>(process ?? null);
  const [failed, setFailed] = useState(!!failedOnly);
  const [shown, setShown] = useState(SHOWN);
  const [openLocal, setOpenLocal] = useState<string | null>(null);
  const open = onExpand ? expanded ?? null : openLocal;
  const setOpen = (id: string | null) => (onExpand ? onExpand(id) : setOpenLocal(id));
  useEffect(() => setProc(process ?? null), [process]);
  useEffect(() => setFailed(!!failedOnly), [failedOnly]);
  useEffect(() => setShown(SHOWN), [proc, failed, runs]);

  const ofProcess = useMemo(() => runs.filter((x) => !proc || (x.process ?? NO_PROCESS) === proc), [runs, proc]);
  // What the lens says is about the documents the search found: a run is failed here when one of them failed in it
  const rows = useMemo(() => ofProcess.filter((x) => !failed || foundFailed(x) > 0), [ofProcess, failed]);
  const nFailed = ofProcess.filter((x) => foundFailed(x) > 0).length;
  const failedOf = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of runs) if (foundFailed(r) > 0) m.set(r.process ?? NO_PROCESS, (m.get(r.process ?? NO_PROCESS) ?? 0) + 1);
    return m;
  }, [runs]);
  const chooseProcess = (p: string | null) => {
    setProc(p);
    onProcess?.(p);
  };
  const chooseFailed = (v: boolean) => {
    setFailed(v);
    onFailedOnly?.(v);
  };
  const cur = processes.find((p) => p.process === proc);
  const title = cur ? stripSystem(cur.process) : proc ? stripSystem(proc) : 'all integrations';
  const sentence = (
    <>
      <NounCount kind="run" n={rows.length} />
      {nFailed > 0 && !failed && <> · <span className="text-fail">{count(nFailed)} failed</span></>}
      {!proc && total !== undefined && total > runs.length && <> · newest {count(runs.length)} of {count(total)}</>}
    </>
  );

  return (
    <LensFrame kind="run" title={title} count={sentence} onClose={onClose}>
      {(processes.length > 1 || nFailed > 0) && (
        <div className="flex flex-wrap items-center gap-2">
          {processes.length > 1 && (
            <PillTrack>
              <Pill active={!proc} onClick={() => chooseProcess(null)}>all integrations</Pill>
              {processes.map((p) => (
                <Pill key={p.process} active={proc === p.process} onClick={() => chooseProcess(p.process)}>
                  <span className="inline-flex items-center gap-1.5">
                    {p.system && <SystemBadge code={p.system} size={20} />}{stripSystem(p.process)}
                    <span className="text-n7">· <Counted n={p.runs} word={plural(p.runs, 'run')} /></span>
                    {(failedOf.get(p.process) ?? 0) > 0 && <span className="text-fail">· <Counted n={failedOf.get(p.process)!} word="failed" /></span>}
                  </span>
                </Pill>
              ))}
            </PillTrack>
          )}
          {nFailed > 0 && (
            <PillTrack>
              <Pill active={failed} onClick={() => chooseFailed(true)}><Counted n={nFailed} word="failed" /></Pill>
              <Pill active={!failed} onClick={() => chooseFailed(false)}>all <Counted n={ofProcess.length} word={plural(ofProcess.length, 'run')} /></Pill>
            </PillTrack>
          )}
        </div>
      )}

      {rows.length === 0 ? (
        <EmptyLens>{runs.length === 0 ? 'The documents found do not belong to any run.' : 'No runs like that here.'}</EmptyLens>
      ) : (
        <ol className="m-0 p-0 list-none flex flex-col">
          {rows.slice(0, shown).map((x, n) => (
            <RunRow key={x.executionId} run={x} n={n} open={open === x.executionId} onOpen={() => onRun(x.executionId, x)}
              onToggle={loadExecution ? () => setOpen(open === x.executionId ? null : x.executionId) : undefined} loadExecution={loadExecution} onMessage={onMessage} />
          ))}
        </ol>
      )}
      {rows.length > shown && <MorePill onClick={() => setShown((s) => s + 100)}>show {count(Math.min(100, rows.length - shown))} more</MorePill>}
    </LensFrame>
  );
}

/** Failed documents among those the search found in the run. */
const foundFailed = (r: Run) => r.statuses.FAILED ?? 0;

function RunRow({ run, n, open, onOpen, onToggle, loadExecution, onMessage }: {
  run: Run; n: number; open: boolean; onOpen: () => void; onToggle?: () => void;
  loadExecution?: (id: string) => Promise<Execution>; onMessage: (ref: MessageRef) => void;
}) {
  // The documents the search found in the run: their count and status (the whole run is in its steps and its peek)
  const found = foundFailed(run);
  const status = runStatus({ failed: found, statuses: run.statuses });
  const others = run.total - run.messages;
  return (
    <li className={cx('border-b border-n4 mv-rise', open && 'bg-n3/40 rounded-chip')} style={{ animationDelay: `${Math.min(n, 20) * 12}ms` }}>
      <div className="flex items-stretch">
        {/* Each row is its own grid with the same template: only the name and the documents columns flex, so every row lines up */}
        <button type="button" onClick={onOpen}
          className="flex-1 min-w-0 grid grid-cols-[16px_minmax(0,1.4fr)_9.5rem_4rem_minmax(0,1fr)_11rem] items-center gap-x-4 py-2.5 pl-1 pr-2 text-left bg-transparent border-0 cursor-pointer text-body text-n10 rounded-chip transition-colors duration-[var(--t-micro)] ease-mv hover:bg-n3/60">
          <NounIcon kind="run" size={16} />
          <span className="inline-flex items-center gap-2 min-w-0">
            {run.system && <SystemBadge code={run.system} size={20} />}
            <span className="truncate font-medium">{stripSystem(run.process) || 'no process'}</span>
          </span>
          <span className="font-mono text-small text-n8 whitespace-nowrap tabular-nums">{compact(run.first, true)}</span>
          <span className="font-mono text-small text-n7 whitespace-nowrap text-right tabular-nums">{run.last > run.first ? duration(run.last - run.first) : ''}</span>
          <span className="text-small text-n8 truncate" title={others > 0 ? `Your search found ${count(run.messages)} of the run's ${count(run.total)} documents` : undefined}>
            {count(run.messages)} {plural(run.messages, 'document')}
          </span>
          <span className="inline-flex items-center justify-end gap-2 whitespace-nowrap">
            {status === 'FAILED' && run.messages > found ? (
              <span className="inline-flex items-center gap-1.5 text-small text-fail">
                <i className="size-1.5 rounded-full shrink-0 bg-fail" aria-hidden="true" />
                <span className="font-mono tabular-nums">{count(found)}</span> of <span className="font-mono tabular-nums">{count(run.messages)}</span> failed
              </span>
            ) : <StatusWord status={status} />}
          </span>
        </button>
        {onToggle && (
          <IconButton label={open ? 'Hide the steps' : 'Show the steps'} active={open} onClick={onToggle} className="self-center ml-1">
            <ChevronIcon size={14} className={cx('transition-transform duration-[var(--t-std)] ease-mv', open && 'rotate-180')} />
          </IconButton>
        )}
      </div>
      {open && loadExecution && (
        <div className="pl-8 pr-3 pb-3 mv-rise">
          {(others > 0 || run.failed > found) && (
            <p className="m-0 pt-1 pb-2 text-small text-n8" data-whole-run="">
              The whole run: {count(run.total)} {plural(run.total, 'document')}, {count(run.messages)} of them found by your search
              {run.failed > found && <> · <span className="text-fail">{count(run.failed - found)} other {plural(run.failed - found, 'document')} failed in it</span></>}
            </p>
          )}
          <Steps id={run.executionId} load={loadExecution} onMessage={onMessage} />
        </div>
      )}
    </li>
  );
}

/** The checkpoints of one run on a time line, each with its status, what it said and the documents it stored. */
export function Steps({ id, load, onMessage }: { id: string; load: (id: string) => Promise<Execution>; onMessage: (ref: MessageRef) => void }) {
  const res = useAsync(() => load(id), [id]);
  if (!res.data) {
    return res.error
      ? <p className="m-0 py-2 text-small text-fail">{res.error}</p>
      : <div className="flex flex-col gap-2 py-2">{[0, 1].map((i) => <span key={i} className="h-8 rounded-chip mv-shimmer" style={{ animationDelay: `${i * 80}ms` }} />)}</div>;
  }
  const e = res.data;
  if (e.steps.length === 0) return <p className="m-0 py-2 text-small text-n8"><Counted n={e.messages} word={plural(e.messages, 'document')} /> · no steps recorded</p>;
  return (
    <ol className="m-0 p-0 list-none flex flex-col">
      {e.steps.map((st, i) => {
        const dot = st.status === 'FAILED' ? 'bg-fail' : st.status === 'HOLD' ? 'bg-warn' : st.status === 'SUCCESS' ? 'bg-ok' : 'bg-n5';
        return (
          <li key={i} className="grid grid-cols-[72px_16px_minmax(0,1fr)] gap-x-3">
            <span className="font-mono text-small text-n8 text-right pt-2 leading-tight">
              {dayjs(st.timestamp).format('HH:mm:ss')}
              {st.elapsedMs !== undefined && <span className="block text-micro text-n7">+{duration(st.elapsedMs)}</span>}
            </span>
            <span className="relative flex justify-center">
              {i < e.steps.length - 1 && <span className="absolute top-4 bottom-0 w-px bg-n4" aria-hidden="true" />}
              <span className={cx('relative mt-2.5 size-2 rounded-full', dot)} aria-hidden="true" />
            </span>
            <span className="flex flex-col gap-1 min-w-0 pt-1.5 pb-3">
              <span className="flex items-center gap-2 flex-wrap text-body">
                <span className="font-medium">{noun(st.messageClass, st.items.length || 1)}</span>
                <StatusWord status={st.status} />
                {st.items.length > 1 && <span className="text-small text-n7">{st.items.length} documents at once</span>}
              </span>
              {st.info && <span className={cx('text-small', st.status === 'FAILED' ? 'text-fail' : 'text-n8')}>{st.info}</span>}
              {st.items.length > 0 && (
                <span className="flex flex-wrap gap-1.5 pt-0.5">
                  {st.items.slice(0, 12).map((h) => (
                    <Chip key={h.uuid} icon={<Glyph format={h.type} size={14} failed={h.meta.status === 'FAILED'} />}
                      onClick={() => onMessage({ vaultId: h.vaultId ?? st.vaultId, uuid: h.uuid, day: h.day })}>
                      <span className="font-mono">{h.uuid.slice(0, 8)}</span>
                    </Chip>
                  ))}
                  {st.items.length > 12 && <span className="text-small text-n7 self-center">+ {st.items.length - 12}</span>}
                </span>
              )}
            </span>
          </li>
        );
      })}
    </ol>
  );
}
